/**
 * Vault (files) advanced-filter document — a versioned value the web filter
 * panel edits and evaluates CLIENT-SIDE over the complete file index. One
 * schema + ONE evaluator, both in `@releve/shared` (the places/Impact/AR/AP/
 * bank-feed template), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyVaultFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build; same
 * posture here). There is NO seeded default either: the Vault opens on every
 * file, so `EMPTY_VAULT_FILTER_DOCUMENT` is the only starting value (the
 * bank feed's `DEFAULT_*` quirk is bank-feed-only).
 *
 * ── v1: THIS IS A MOVE, NOT AN EXPANSION ───────────────────────────────────
 * v1's vocabulary is EXACTLY the five toolbar filters the legacy
 * `FilesDataGrid` carried — file type, source, tags, project and the created
 * date range — so the conversion to the panel is behaviour-preserving and
 * reviewable. Nothing net-new (no size ranges, no content-type family).
 *
 * The one addition since: `file.attached_kinds` (the "Attached to" filter,
 * migration 20260925180000) — an ARRAY field like tags, additive under the
 * same `version: 1` because documents are session-only (component state), so
 * no stored document can predate it.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The Vault grid ends with ZERO filterable columns.
 *
 * v1 fields are `file.<field>` over the MAPPED grid rows, not the wire row —
 * `source` in particular does not exist on the view at all; the mapper derives
 * it from `is_generated_report`. Evaluation runs AFTER the mapper and BEFORE
 * the grid shell (`hasUpstreamFilters`). The field string carries its
 * namespace, so a later vocabulary stays additive.
 */
import { z } from "zod";
import { makeArrayConditionSchema, makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesArrayOp, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the five ex-toolbar filters + attached kinds
// ---------------------------------------------------------------------------
/**
 * `file.source` is MAPPER-DERIVED ('generated' | 'uploaded'), not a view
 * column — see the row shape below. `file.file_type` carries the view's OPEN
 * vocabulary, so `values` stays a plain string list.
 */
export const VAULT_FILTER_STRING_FIELDS = [
    "file.file_type",
    "file.source",
    "file.project_name",
];
/** The one `text[]` family — the file's tags. */
export const VAULT_FILTER_ARRAY_FIELDS = ["file.tags", "file.attached_kinds"];
export const VAULT_FILTER_DATE_FIELDS = ["file.created_at"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * THREE families, so a three-armed union. The arms have DISJOINT op sets
 * ('in'/'is_empty' vs 'array_has_any'/'array_is_empty' vs the three date ops),
 * which is what lets `matchesCondition` narrow on `op` alone — and the reason
 * the array ops are namespaced rather than reusing `is_empty`.
 *
 * EMPTY-SET SEMANTICS (from the shared primitives, not re-decided here): an
 * `in` or `array_has_any` condition with zero values is REJECTED by the
 * schema. "Nothing checked" is spelled as NO CONDITION AT ALL — the panel
 * drops the family — and never as a condition that would match nothing.
 */
const stringConditionSchema = makeStringConditionSchema(VAULT_FILTER_STRING_FIELDS);
const arrayConditionSchema = makeArrayConditionSchema(VAULT_FILTER_ARRAY_FIELDS);
const dateConditionSchema = makeDateConditionSchema(VAULT_FILTER_DATE_FIELDS);
export const vaultFilterConditionSchema = z.union([
    stringConditionSchema,
    arrayConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const vaultFilterDocumentSchema = makeFilterDocumentSchema(vaultFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_VAULT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary with NO default case (the reason `sustainabilityFilters` keeps
 * them: a new vocabulary entry is a `TS2366: Function lacks ending return
 * statement` until it is handled here, instead of compiling clean and
 * filtering the wrong column).
 */
function stringValue(row, field) {
    switch (field) {
        case "file.file_type":
            return row.file_type;
        case "file.source":
            return row.source;
        case "file.project_name":
            return row.project_name ?? "";
    }
}
function arrayValue(row, field) {
    switch (field) {
        case "file.tags":
            return row.tags;
        case "file.attached_kinds":
            return row.attached_kinds;
    }
}
/**
 * The LOCAL calendar day (`yyyy-MM-dd`) to compare. `created_at` is a raw
 * `timestamptz`, so it goes through `toLocalCalendarDay` — the local-day
 * reduction matches what the legacy date-range `filterFn`'s `Date` comparison
 * bracketed and what the cell renders. Never an ISO prefix slice (that is the
 * UTC day, off by one either side of midnight). Compared as STRINGS —
 * lexicographic order is date order for this shape.
 */
function dateValue(row, field) {
    switch (field) {
        case "file.created_at":
            return toLocalCalendarDay(row.created_at);
    }
}
// The three condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "array_has_any":
        case "array_is_empty":
            return matchesArrayOp(c.op, arrayValue(row, c.field), c.values);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped Vault rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyVaultFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveVaultConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=vaultFilters.js.map